import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const services = [
  { title: 'Montagem Mecânica de Equipamentos', desc: 'Execução de montagem de componentes, equipamentos e sistemas em prensas, guilhotinas, esteiras e grandes máquinas em Serra.' },
  { title: 'Desmontagem Controlada', desc: 'Desmontagem segura e controlada de equipamentos durante paradas de manutenção programadas em plantas industriais.' },
  { title: 'Alinhamento de Equipamentos', desc: 'Alinhamento técnico de componentes rotativos e estáticos para garantir performance operacional em sistemas de grande porte.' },
  { title: 'Troca de Componentes Mecânicos', desc: 'Substituição de componentes desgastados ou danificados em equipamentos de siderurgia, mineração e metal mecânica.' },
  { title: 'Ajustes e Calibrações', desc: 'Ajustes finos e calibração de equipamentos para otimização de performance e redução de folgas em processos industriais.' },
  { title: 'Inspeções Técnicas Preventivas', desc: 'Inspeções preventivas para identificar condições e prever necessidades de manutenção em equipamentos críticos.' },
  { title: 'Recuperação de Equipamentos', desc: 'Apoio técnico em campo para recuperação de equipamentos danificados ou com baixa performance em operações industriais.' },
  { title: 'Apoio à Paradas de Manutenção', desc: 'Equipes dedicadas para apoiar manutenções programadas em grandes plantas industriais da região de Serra.' },
];

const segments = [
  'Siderurgia',
  'Metal Mecânica',
  'Mineração',
  'Logística Industrial',
  'Grandes Plantas Industriais',
  'Transformação Metalúrgica',
];

const differentials = [
  'Atendimento rápido para Serra e polos industriais da Grande Vitória',
  'Equipes especializadas em montagem, desmontagem e alinhamento mecânico',
  'Experiência em paradas de manutenção em grandes plantas industriais',
  'Logística otimizada para reduzir tempo de parada',
  'Foco em segurança, confiabilidade e conformidade normativa',
  'Parceiros homologados para processos especializados',
];

const faqs = [
  { q: 'A Pilar atende diretamente em Serra?', a: 'Sim, a Pilar realiza atendimento diretamente na região de Serra, com equipe de campo própria para montagem, desmontagem, alinhamento e apoio técnico em operações mecânicas industriais.' },
  { q: 'Quais tipos de equipamentos são atendidos em Serra?', a: 'Equipamentos de siderurgia, prensas, guilhotinas, dobradeiras, esteiras transportadoras, guindastes, máquinas de mineração e sistemas mecânicos de grande porte.' },
  { q: 'A Pilar trabalha em paradas de manutenção programadas?', a: 'Sim, a Pilar participa ativamente de paradas de manutenção programadas em grandes plantas industriais, com equipe escalada e planejamento de atividades conforme cronograma do cliente.' },
];

export default function SerraMecanicaPage() {
  useSeo({
    title: 'Mecânica Industrial em Serra | Serviços de Campo | Pilar',
    description: 'Serviços de mecânica industrial em Serra e região. Montagem, desmontagem, alinhamento, troca de componentes e apoio técnico para siderurgia, metal mecânica, mineração e grandes plantas industriais da Grande Vitória.',
    keywords: 'mecânica industrial Serra, manutenção mecânica Serra, serviços mecânicos Grande Vitória, montagem mecânica siderurgia Serra, alinhamento de equipamentos ES, Grupo Pilar Serra',
    canonical: 'https://www.pilarmanutencao.com.br/mecanica-industrial-serra',
    ogUrl: 'https://www.pilarmanutencao.com.br/mecanica-industrial-serra',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/LOCAL/SERRA/serra-mecanica-hero.jpg"
              alt="Mecânica industrial em Serra - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Serra - Grande Vitória</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Mecânica</span> Industrial em Serra
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Serviços de mecânica industrial em campo para siderurgia, metal mecânica, mineração e grandes plantas industriais da região de Serra.
              </p>
            </div>
          </div>
        </section>

        {/* Introdução Local */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atuação em Serra e Região</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Serra abriga importantes indústrias de siderurgia, metal mecânica e mineração, que operam com equipamentos de grande porte e alta demanda. A Pilar oferece serviços de mecânica industrial em campo, com equipe especializada em montagem, desmontagem, alinhamento e apoio técnico.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Atuamos em paradas de manutenção programadas, intervenções corretivas e apoio técnico contínuo, com logística otimizada para a região da Grande Vitória.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Serviços de Mecânica em Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Segmentos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos em Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Diferenciais da Pilar para Serra</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {differentials.map((diff, index) => (
                <div key={index} className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Perguntas Frequentes</h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gp-graphite border border-white/10 rounded-lg p-6">
                    <h3 className="font-display text-base font-bold text-white uppercase mb-3">{faq.q}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio em mecânica industrial em Serra?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e conte com o Grupo Pilar para apoiar sua operação com serviços mecânicos de campo na região da Grande Vitória.
                </p>
                <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap">
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Apoio Mecânico</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Links Internos */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/mecanica-industrial" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Página Principal do Serviço
              </Link>
              <Link to="/contato" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                Contato
              </Link>
              <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}